import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import Header from './Header';
import Footer from './Footer';
import { useUser } from '../contexts/UserContext';
import './BlogPost.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

// Slugifies a heading for use as an anchor id. Same rules as the backend's
// slugify so admin-typed slugs and auto-anchored TOC entries align.
function headingSlug(text) {
  return (text || '').toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

// Extract H2/H3 headings from raw markdown for the sticky TOC. Cheap and
// good enough — we don't try to parse code-blocks for false-positive '##'.
function extractToc(md) {
  if (!md) return [];
  const out = [];
  let inCode = false;
  for (const line of md.split('\n')) {
    if (line.trim().startsWith('```')) { inCode = !inCode; continue; }
    if (inCode) continue;
    const m = /^(#{2,3})\s+(.+)$/.exec(line);
    if (m) out.push({ level: m[1].length, text: m[2].trim(), id: headingSlug(m[2]) });
  }
  return out;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
}

function PostCodeBlock({ language, value }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="terview-code-block">
      <div className="terview-code-header">
        <span className="terview-code-lang">{language || 'code'}</span>
        <button
          type="button"
          className="terview-code-copy"
          onClick={() => {
            try {
              navigator.clipboard.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            } catch { /* clipboard blocked */ }
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <SyntaxHighlighter
        language={language || 'text'}
        style={oneDark}
        showLineNumbers
        wrapLongLines
        customStyle={{
          margin: 0,
          padding: '14px 16px',
          background: '#0d1117',
          fontSize: '0.88em',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
        codeTagProps={{ style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' } }}
      >
        {value.replace(/\n$/, '')}
      </SyntaxHighlighter>
    </div>
  );
}

// Markdown component overrides: heading anchors for TOC linking, code blocks
// routed through SyntaxHighlighter (same look as the interview pane).
function makeMdComponents() {
  const headingRenderer = (Tag) => ({ children }) => {
    const text = React.Children.toArray(children).map(c =>
      typeof c === 'string' ? c : (c.props?.children || '')
    ).join('');
    const id = headingSlug(text);
    return <Tag id={id} className="blog-post-h"><a href={`#${id}`} className="blog-post-h-anchor">{children}</a></Tag>;
  };
  return {
    h1: headingRenderer('h1'),
    h2: headingRenderer('h2'),
    h3: headingRenderer('h3'),
    code({ inline, className, children }) {
      const match = /language-(\w+)/.exec(className || '');
      const value = String(children ?? '');
      if (!inline && (match || value.includes('\n'))) {
        return <PostCodeBlock language={match ? match[1] : ''} value={value} />;
      }
      return <code className="terview-inline-code">{children}</code>;
    },
    a({ href, children }) {
      const external = href && /^https?:\/\//.test(href);
      return external
        ? <a href={href} target="_blank" rel="noopener noreferrer" className="blog-post-link">{children}</a>
        : <a href={href} className="blog-post-link">{children}</a>;
    },
  };
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeId, setActiveId] = useState('');
  const articleRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/api/blog/${encodeURIComponent(slug)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async r => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Not found');
        return r.json();
      })
      .then(data => { if (!cancelled) setPost(data.post); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const toc = useMemo(() => extractToc(post?.content_md), [post]);
  const mdComponents = useMemo(() => makeMdComponents(), []);

  // Active-section highlighting in the TOC.
  useEffect(() => {
    if (!articleRef.current || toc.length === 0) return;
    const headings = Array.from(articleRef.current.querySelectorAll('h1[id], h2[id], h3[id]'));
    if (headings.length === 0) return;
    const obs = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    );
    headings.forEach(h => obs.observe(h));
    return () => obs.disconnect();
  }, [toc, post]);

  if (loading) {
    return (
      <div className="blog-post-page">
        <Header />
        <div className="blog-post-loading">Loading…</div>
        <Footer />
      </div>
    );
  }
  if (error || !post) {
    return (
      <div className="blog-post-page">
        <Header />
        <div className="blog-post-error">
          <h2>Post not found</h2>
          <p>{error || 'This article may have been moved or unpublished.'}</p>
          <Link to="/blog" className="blog-post-back-link">← Back to blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="blog-post-page">
      <Header />

      <div className="blog-post-container">
        <nav className="blog-breadcrumbs">
          <Link to="/">Home</Link>
          <span>›</span>
          <Link to="/blog">Blog</Link>
          <span>›</span>
          <span className="blog-breadcrumbs-current">{post.title}</span>
        </nav>

        {/* Hero card */}
        <div className={`blog-post-hero glass ${post.hero_image_url ? '' : 'no-image'}`}>
          {post.hero_image_url && (
            <div className="blog-post-hero-img">
              <img src={post.hero_image_url} alt={post.title} />
            </div>
          )}
          <div className="blog-post-hero-text">
            {post.status === 'draft' && (
              <span className="blog-post-draft-tag">DRAFT — only admins can see this</span>
            )}
            <h1 className="blog-post-title">{post.title}</h1>
            {post.summary && <p className="blog-post-summary">{post.summary}</p>}
            <div className="blog-post-meta-row">
              <div className="blog-post-meta-block">
                <div className="blog-post-meta-label">WRITTEN BY</div>
                <div className="blog-post-meta-author">
                  <span className="blog-avatar-fallback" style={{ width: 28, height: 28, fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: 'linear-gradient(135deg, #6d4f9f, #4a7bb1)', color: '#fff', fontWeight: 700, marginRight: 8 }}>
                    {(post.author_name || '?').charAt(0).toUpperCase()}
                  </span>
                  {post.author_name}
                </div>
              </div>
              <div className="blog-post-meta-block">
                <div className="blog-post-meta-label">UPDATED ON</div>
                <div className="blog-post-meta-author">
                  {formatDate(post.published_at || post.updated_at)} · {post.read_minutes || 1} min read
                </div>
              </div>
            </div>
            {user?.is_admin && (
              <div className="blog-post-admin-actions">
                <button
                  type="button"
                  className="blog-post-admin-btn"
                  onClick={() => navigate(`/blog/admin/edit/${post.id}`)}
                >
                  ✎ Edit
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Body: TOC + content */}
        <div className="blog-post-body">
          {toc.length > 0 && (
            <aside className="blog-post-toc glass">
              <div className="blog-post-toc-title">TABLE OF CONTENTS</div>
              <ul>
                {toc.map(h => (
                  <li
                    key={h.id}
                    className={`toc-level-${h.level} ${activeId === h.id ? 'active' : ''}`}
                  >
                    <a href={`#${h.id}`}>{h.text}</a>
                  </li>
                ))}
              </ul>
            </aside>
          )}

          <article ref={articleRef} className={`blog-post-article ${toc.length === 0 ? 'no-toc' : ''}`}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {post.content_md || ''}
            </ReactMarkdown>
          </article>
        </div>
      </div>

      <Footer />
    </div>
  );
}
